import React, { useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface RatingStarsProps {
  value: number;
  onChange?: (value: number) => void;
  readOnly?: boolean;
  size?: "small" | "medium" | "large";
  showValue?: boolean;
  label?: string;
  className?: string;
}

const SIZE_MAP = {
  small: 20,
  medium: 28,
  large: 36,
};

export default function RatingStars({
  value,
  onChange,
  readOnly = false,
  size = "medium",
  showValue = false,
  label,
  className,
}: RatingStarsProps) {
  const [hover, setHover] = useState<number | null>(null);
  const display = hover ?? value;
  const px = SIZE_MAP[size];

  return (
    <div className={className}>
      {label && <p className="mb-1.5 text-sm font-medium text-foreground">{label}</p>}
      <div className="flex items-center gap-2">
        <div
          className="flex items-center gap-0.5"
          role={readOnly ? "img" : "radiogroup"}
          aria-label={readOnly ? `Rated ${value} out of 5` : label || "Rating"}
          onMouseLeave={() => setHover(null)}
        >
          {[1, 2, 3, 4, 5].map((n) => {
            const filled = n <= Math.round(display);
            const star = (
              <Star
                size={px}
                strokeWidth={1.5}
                className={cn(
                  "transition-colors",
                  filled ? "fill-warning text-warning" : "text-border",
                )}
                aria-hidden
              />
            );
            if (readOnly) return <span key={n}>{star}</span>;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={value === n}
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                className="rounded-sm p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onMouseEnter={() => setHover(n)}
                onFocus={() => setHover(n)}
                onBlur={() => setHover(null)}
                onClick={() => onChange?.(value === n ? 0 : n)}
              >
                {star}
              </button>
            );
          })}
        </div>
        {showValue && (
          <span
            className={cn(
              "font-medium tabular-nums text-foreground",
              size === "small" ? "text-sm" : "text-base",
            )}
          >
            {value.toFixed(1)}
          </span>
        )}
      </div>
    </div>
  );
}
